import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { RemoveCircleOutline as DeleteIcon } from '@mui/icons-material';
import moment from 'moment';

import {
  FBoxContainer,
  FHAntdDateTimePicker,
  FHInputField,
  FTypography,
  FHSelect,
  FSwitch,
} from '../../../base';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { abaTherapyServicesActions } from '../../../../store';
import { getAllInvalidFieldNamesAuto, validateSchema } from '../../../../helpers';
import { BackgroundTypes, Themes } from '../../../../constants';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import {
  COLLABORATION_OF_CARE_TEXT,
  EFrequency,
  ERecordShared,
  EResponseToTreatment,
  FREQUENCY,
  TREATMENT_RESPONSE,
  errorStateConstants,
} from './constants';
import { getThemeColor } from '../../../../utils/common';
import { TherapyServiceSchema } from './validationSchema';

import type { IAbaTherapyService, SelectValueType } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';

interface TherapyRowProps {
  service: IAbaTherapyService;
  onUpdate: (service: IAbaTherapyService) => void;
  onRemove: () => void;
}

function isFrequency(value: SelectValueType): value is EFrequency {
  return Object.values(EFrequency).some((option) => option === value);
}

function isResponseToTreatment(value: SelectValueType): value is EResponseToTreatment {
  return Object.values(EResponseToTreatment).some((option) => option === value);
}

function isRecordShared(value: SelectValueType): value is ERecordShared {
  return Object.values(ERecordShared).some((option) => option === value);
}

function TherapyHistoryRow({ service, onUpdate, onRemove }: TherapyRowProps) {
  const dispatch = useDispatch();
  const isEditable = useTreatmentPlanEditable();
  const { current: tp } = useSelector((state: RootState) => state.treatmentPlans);
  const { triggerValidationsOnPageLoad } = useSelector(
    (state: RootState) => state.abaTherapyServices,
  );
  const [abaService, setAbaService] = useState<IAbaTherapyService>(service);
  const [errors, setErrors] = useState<any>({});

  const listOfValidationKeys = [
    'provider_name',
    'service_type',
    'date_start',
    'date_end',
    'hours',
    'frequency',
    'is_parents_authorization_provided',
    'response_to_treatment',
    'is_record_shared',
  ];

  useEffect(() => {
    if (!abaService) return;

    const {
      frequency: _frequency,
      response_to_treatment: _response_to_treatment,
      is_record_shared: _is_record_shared,
      ...fieldsToBeValidated
    } = abaService;

    //Removing the 3 fields that are being prefilled
    if (!fieldsToBeValidated || Object.keys(fieldsToBeValidated).length === 0) {
      return;
    }

    listOfValidationKeys.forEach((key: string) => {
      handleValidate(key, abaService);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaService]);

  useEffect(() => {
    if (service && triggerValidationsOnPageLoad) {
      const validationErrors = getAllInvalidFieldNamesAuto(TherapyServiceSchema, service);
      setErrors((prev: any) => ({ ...prev, ...validationErrors }));
    }

    setAbaService(service);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  useEffect(() => {
    if (!errors) return;

    updateErrorCount(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const recordSharedOptions = useMemo(() => {
    return ['Shared', 'Will be shared'].map((phiAuth) => ({
      value: phiAuth,
      label: phiAuth,
    }));
  }, []);

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      abaTherapyServicesActions.updateErrorState({
        [`${errorStateConstants.services}-${abaService?.id ? abaService.id : 'new'}`]: errorCount,
      }),
    );
  };

  const handleValidate = (key: string, data: IAbaTherapyService) => {
    if (!data) return;

    const isValid = validateSchema(TherapyServiceSchema, key, data);
    setErrors((prev: any) => ({ ...prev, [key]: !isValid }));
  };

  const handleFieldChange = <K extends keyof IAbaTherapyService>(
    key: K,
    value: IAbaTherapyService[K],
  ) => {
    if (key === 'is_parents_authorization_provided' && !value) {
      setAbaService({
        ...abaService,
        [key]: value,
        is_record_shared: null,
      });
    } else {
      setAbaService({ ...abaService, [key]: value });
    }
  };

  const handleUpdateService = () => {
    if (!abaService) return;
    onUpdate(abaService);
  };

  return (
    <FBoxContainer
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: FORTA_WHITE,
        borderRadius: '10px',
        boxShadow: `0px 4px 4px ${getThemeColor(Themes.Primary).light2}`,
        mb: 3,
      }}
    >
      <Grid container spacing={3} sx={{ alignItems: 'center' }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEditable && (
              <IconButton sx={{ mt: 2, mr: 1 }} onClick={onRemove}>
                <DeleteIcon data-testid="deleteServiceButton" sx={{ color: FORTA_BLUE.primary }} />
              </IconButton>
            )}
            <Box sx={{ width: '100%' }}>
              <Typography fontWeight="bold" mb={1} textAlign="left">
                Provider name*
              </Typography>
              <FHInputField
                data-testid="providerNameServiceTextField"
                fullWidth
                inputHeight={40}
                disabled={!isEditable}
                coloredBackground
                value={abaService?.provider_name ?? ''}
                onChange={(e) => {
                  handleFieldChange('provider_name', e.target.value);
                }}
                onBlur={handleUpdateService}
                error={errors['provider_name']}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={3.5}>
          <Typography textAlign="left" fontWeight="bold" mb={1}>
            Service type*
          </Typography>
          <FHInputField
            data-testid="serviceTypeTextField"
            fullWidth
            disabled={!isEditable}
            inputHeight={40}
            coloredBackground
            value={abaService?.service_type ?? ''}
            onChange={(e) => {
              handleFieldChange('service_type', e.target.value);
            }}
            onBlur={handleUpdateService}
            error={errors['service_type']}
          />
        </Grid>
        <Grid item xs={12} md={4} mt={2} ml={1}>
          <Grid
            container
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Grid item xs={12} md={10}>
              <Typography fontWeight="bold" textAlign="left">
                Guardian&nbsp;
                {abaService?.is_parents_authorization_provided ? (
                  'provided'
                ) : (
                  <>
                    did NOT provide
                    <br />
                  </>
                )}
                &nbsp;&quot;Authorization to Release Information&quot;
              </Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <FSwitch
                width={60}
                height={30}
                checked={abaService?.is_parents_authorization_provided}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  handleFieldChange('is_parents_authorization_provided', event.target.checked);
                }}
                onBlur={handleUpdateService}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={6} md={1.8}>
          <FTypography fontWeight="bold" mb={1} sx={{ ml: 1 }} textAlign="left">
            Start date*
          </FTypography>
          <FHAntdDateTimePicker
            data-testid="serviceStartDatePicker"
            format="MM/YY"
            allowClear={false}
            style={{ width: '100%' }}
            inputHeight={40}
            coloredBackground
            disabledDate={(current: Moment) =>
              tp?.client?.dob ? moment(current).isBefore(moment(tp?.client?.dob), 'day') : false
            }
            disabled={!isEditable}
            picker={'month'}
            value={abaService?.date_start ? moment(abaService?.date_start) : null}
            onChange={(value: Moment) =>
              handleFieldChange('date_start', value?.date(1)?.format('YYYY-MM-DD'))
            }
            onBlur={handleUpdateService}
            error={errors['date_start']}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={1.8}>
          <Typography fontWeight="bold" mb={1} textAlign="left">
            End date
          </Typography>
          <FHAntdDateTimePicker
            data-testid="serviceEndDatePicker"
            format="MM/YY"
            style={{ width: '100%' }}
            coloredBackground
            disabledDate={(current: Moment) =>
              moment(current).isBefore(moment(abaService?.date_start), 'day')
            }
            disabled={!isEditable}
            picker={'month'}
            inputHeight={40}
            allowClear={true}
            value={abaService?.date_end ? moment(abaService?.date_end) : null}
            onChange={(value: Moment) =>
              handleFieldChange('date_end', value?.date(1)?.format('YYYY-MM-DD'))
            }
            onBlur={handleUpdateService}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={1.8}>
          <Typography fontWeight="bold" mb={1} textAlign="left">
            Hours*
          </Typography>
          <FHInputField
            data-testid="serviceHoursTextField"
            fullWidth
            inputHeight={40}
            sx={{ minWidth: 50 }}
            coloredBackground
            disabled={!isEditable}
            type="number"
            value={abaService?.hours ?? ''}
            onChange={(e) => {
              handleFieldChange('hours', parseFloat(e.target.value));
            }}
            onBlur={handleUpdateService}
            error={errors['hours']}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Typography fontWeight="bold" mb={1} textAlign="left">
            Service frequency*
          </Typography>
          <FHSelect
            data-testid="serviceFrequencyDropDown"
            sx={{ minWidth: '100%' }}
            inputHeight={40}
            backgroundType={BackgroundTypes.Colored}
            disabled={!isEditable}
            options={FREQUENCY.map((frequency) => ({
              value: frequency.value,
              label: frequency.label,
            }))}
            optionDataTestIdPrefix="serviceFrequencyDropDown"
            value={abaService?.frequency}
            onBlur={handleUpdateService}
            onUpdate={(value: SelectValueType) => {
              if (isFrequency(value)) handleFieldChange('frequency', value);
            }}
            error={errors['frequency']}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.5}>
          <Typography fontWeight="bold" mb={1} textAlign="left">
            Progress*
          </Typography>
          <FHSelect
            data-testid="reasonForTherapyDropDown"
            sx={{ width: '100%' }}
            inputHeight={40}
            backgroundType={BackgroundTypes.Colored}
            disabled={!isEditable}
            options={TREATMENT_RESPONSE.map((response) => ({
              value: response,
              label: response,
            }))}
            optionDataTestIdPrefix="reasonForTherapyDropDown"
            value={abaService?.response_to_treatment}
            onUpdate={(value: SelectValueType) => {
              if (isResponseToTreatment(value)) handleFieldChange('response_to_treatment', value);
            }}
            onBlur={handleUpdateService}
            error={errors['response_to_treatment']}
          />
        </Grid>
        {abaService?.is_parents_authorization_provided && (
          <Grid item xs={12} sm={6} md={2}>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              Record shared*
            </Typography>
            <FHSelect
              data-testid="isRecordSharedDropdown"
              sx={{ width: '100%' }}
              inputHeight={40}
              backgroundType={BackgroundTypes.Colored}
              disabled={!isEditable}
              options={recordSharedOptions}
              value={abaService?.is_record_shared || ''}
              onUpdate={(value: SelectValueType) => {
                if (isRecordShared(value)) handleFieldChange('is_record_shared', value);
              }}
              onBlur={handleUpdateService}
              error={errors['is_record_shared']}
            />
          </Grid>
        )}
      </Grid>
      <Typography fontSize={{ xs: 14, md: 16 }} textAlign="left" mt={3}>
        {
          COLLABORATION_OF_CARE_TEXT[
            abaService?.is_parents_authorization_provided ? 'authorized' : 'unauthorized'
          ]
        }
      </Typography>
    </FBoxContainer>
  );
}

export default TherapyHistoryRow;
